/**
 * Workflows Management Page
 * 
 * Admin interface for managing workflow templates, steps, fields, and transitions.
 * Monday.com-inspired UI.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  Workflow,
  ChevronRight,
  Settings,
  Trash2,
  Copy,
  MoreVertical,
  ArrowRight,
  CheckCircle2,
  Circle,
  FileText,
  User,
  Clock,
  GripVertical,
  Edit3,
  Save,
  X,
  BarChart3
} from 'lucide-react';
import * as workflowApi from '../api/workflows';

// Utility for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

// Color options for templates
const COLORS = [
  { name: 'blue', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  { name: 'green', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  { name: 'purple', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  { name: 'orange', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  { name: 'red', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  { name: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
];

const getColorClasses = (colorName) => {
  return COLORS.find(c => c.name === colorName) || COLORS[0];
};

// Entity type options
const ENTITY_TYPES = [
  { value: 'part', label: 'Part' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'claim', label: 'Claim' },
];

// Step types
const STEP_TYPES = [
  { value: 'task', label: 'Task', icon: FileText },
  { value: 'approval', label: 'Approval', icon: CheckCircle2 },
  { value: 'review', label: 'Review', icon: FileText },
  { value: 'decision', label: 'Decision', icon: ArrowRight },
];

// Assignment types
const ASSIGNMENT_TYPES = [
  { value: 'role', label: 'Role' },
  { value: 'user', label: 'Specific User' },
  { value: 'entity_owner', label: 'Entity Owner' },
  { value: 'previous_assignee', label: 'Previous Assignee' },
];

// Field types
const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File Upload' },
];

// =============================================================================
// TEMPLATE LIST
// =============================================================================

const TemplateList = ({ templates, onSelect, selectedId, onCreateNew }) => {
  return (
    <div className="w-80 border-r border-gray-200 bg-gray-50 overflow-y-auto">
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Workflow Templates</h2>
        </div>
        <button
          onClick={onCreateNew}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          New Template
        </button>
      </div>
      
      <div className="p-2">
        {templates?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Workflow size={40} className="mx-auto mb-2 opacity-50" />
            <p>No templates yet</p>
            <p className="text-sm">Create your first workflow</p>
          </div>
        ) : (
          templates?.map(template => {
            const colors = getColorClasses(template.color);
            return (
              <button
                key={template.id}
                onClick={() => onSelect(template)}
                className={cn(
                  "w-full text-left p-3 rounded-lg mb-1 transition-colors",
                  selectedId === template.id
                    ? "bg-white shadow-sm border border-gray-200"
                    : "hover:bg-white/60"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bg)}>
                    <Workflow size={20} className={colors.text} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{template.name}</div>
                    <div className="text-xs text-gray-500">
                      {template.entity_types?.length || 0} entities
                      {!template.is_active && <span className="ml-2 text-orange-500">(Inactive)</span>}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-400" />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// =============================================================================
// TEMPLATE EDITOR
// =============================================================================

const TemplateEditor = ({ template, onUpdate, onDelete }) => {
  const queryClient = useQueryClient();
  const [editingStep, setEditingStep] = useState(null);
  const [showAddStep, setShowAddStep] = useState(false);
  const [showAddField, setShowAddField] = useState(null);
  
  // Mutation for adding steps
  const addStepMutation = useMutation({
    mutationFn: ({ templateId, stepData }) => workflowApi.addStep(templateId, stepData),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-template', template.id]);
      toast.success('Step added successfully');
      setShowAddStep(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to add step');
    }
  });
  
  // Mutation for updating steps
  const updateStepMutation = useMutation({
    mutationFn: ({ stepId, stepData }) => workflowApi.updateStep(stepId, stepData),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-template', template.id]);
      toast.success('Step updated');
      setEditingStep(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update step');
    }
  });
  
  // Mutation for deleting steps
  const deleteStepMutation = useMutation({
    mutationFn: (stepId) => workflowApi.deleteStep(stepId),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-template', template.id]);
      toast.success('Step deleted');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete step');
    }
  });
  
  // Mutation for adding fields
  const addFieldMutation = useMutation({
    mutationFn: ({ stepId, fieldData }) => workflowApi.addField(stepId, fieldData),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-template', template.id]);
      toast.success('Field added');
      setShowAddField(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to add field');
    }
  });
  
  // Mutation for adding transitions
  const addTransitionMutation = useMutation({
    mutationFn: ({ templateId, transitionData }) => workflowApi.addTransition(templateId, transitionData),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-template', template.id]);
      toast.success('Transition added');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to add transition');
    }
  });
  
  const handleAddStep = (stepData) => {
    const nextOrder = (template.steps?.length || 0) + 1;
    addStepMutation.mutate({
      templateId: template.id,
      stepData: {
        ...stepData,
        step_order: nextOrder
      }
    });
  };
  
  const handleDeleteStep = (stepId) => {
    if (window.confirm('Are you sure you want to delete this step?')) {
      deleteStepMutation.mutate(stepId);
    }
  };
  
  const handleAddTransition = (fromStepId, toStepId, actionName) => {
    addTransitionMutation.mutate({
      templateId: template.id,
      transitionData: {
        from_step_id: fromStepId,
        to_step_id: toStepId,
        action_name: actionName,
        action_color: actionName === 'approve' ? 'green' : actionName === 'reject' ? 'red' : 'blue',
        is_default: actionName === 'approve'
      }
    });
  };
  
  if (!template) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <Workflow size={48} className="mx-auto mb-4 opacity-50" />
          <p>Select a template to edit</p>
        </div>
      </div>
    );
  }
  
  const colors = getColorClasses(template.color);
  
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", colors.bg)}>
              <Workflow size={24} className={colors.text} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{template.name}</h1>
              <p className="text-sm text-gray-500">
                {template.description || 'No description'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDelete(template.id)}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete template"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
        
        {/* Entity Types */}
        <div className="mt-4 flex items-center gap-2">
          <span className="text-sm text-gray-600">Applies to:</span>
          {template.entity_types?.map(type => (
            <span key={type} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
              {type}
            </span>
          ))}
        </div>
      </div>
      
      {/* Steps */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Workflow Steps</h2>
          <button
            onClick={() => setShowAddStep(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Plus size={16} />
            Add Step
          </button>
        </div>
        
        {/* Step List */}
        <div className="space-y-3">
          {template.steps?.sort((a, b) => a.step_order - b.step_order).map((step, index) => (
            <StepCard
              key={step.id}
              step={step}
              index={index}
              isLast={index === template.steps.length - 1}
              onEdit={() => setEditingStep(step)}
              onDelete={() => handleDeleteStep(step.id)}
              onAddField={() => setShowAddField(step.id)}
              onAddTransition={(toStepId, actionName) => handleAddTransition(step.id, toStepId, actionName)}
              nextSteps={template.steps?.filter(s => s.step_order > step.step_order)}
              transitions={template.transitions?.filter(t => t.from_step_id === step.id) || []}
            />
          ))}
          
          {template.steps?.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <Circle size={40} className="mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500">No steps defined yet</p>
              <button
                onClick={() => setShowAddStep(true)}
                className="mt-2 text-blue-600 hover:underline text-sm"
              >
                Add your first step
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Add Step Modal */}
      {showAddStep && (
        <AddStepModal
          onClose={() => setShowAddStep(false)}
          onSave={handleAddStep}
        />
      )}
      
      {/* Edit Step Modal */}
      {editingStep && (
        <EditStepModal
          step={editingStep}
          onClose={() => setEditingStep(null)}
          onSave={(stepData) => updateStepMutation.mutate({ stepId: editingStep.id, stepData })}
        />
      )}
      
      {/* Add Field Modal */}
      {showAddField && (
        <AddFieldModal
          stepId={showAddField}
          onClose={() => setShowAddField(null)}
          onSave={(fieldData) => addFieldMutation.mutate({ stepId: showAddField, fieldData })}
        />
      )}
    </div>
  );
};

// =============================================================================
// STEP CARD
// =============================================================================

const StepCard = ({ step, index, isLast, onEdit, onDelete, onAddField, onAddTransition, nextSteps, transitions }) => {
  const StepIcon = STEP_TYPES.find(t => t.value === step.step_type)?.icon || FileText;
  const [showTransitionMenu, setShowTransitionMenu] = useState(false);
  
  return (
    <div className="relative">
      {/* Connector line */}
      {!isLast && (
        <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-gray-200 -mb-3" />
      )}
      
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Step number */}
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold">
              {index + 1}
            </div>
            
            {/* Step details */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">{step.name}</h3>
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {STEP_TYPES.find(t => t.value === step.step_type)?.label || step.step_type}
                </span>
                {step.requires_signoff && (
                  <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-600 rounded">
                    Sign-off Required
                  </span>
                )}
              </div>
              
              {step.description && (
                <p className="text-sm text-gray-500 mt-1">{step.description}</p>
              )}
              
              {/* Assignment info */}
              <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {step.assignment_type === 'role' && step.assigned_role}
                  {step.assignment_type === 'user' && 'Specific User'}
                  {step.assignment_type === 'entity_owner' && 'Entity Owner'}
                  {step.assignment_type === 'previous_assignee' && 'Previous Assignee'}
                </span>
                {step.due_days && (
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {step.due_days} days
                  </span>
                )}
              </div>
              
              {/* Fields */}
              {step.fields?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">Custom Fields:</div>
                  <div className="flex flex-wrap gap-2">
                    {step.fields.map(field => (
                      <span key={field.id} className="px-2 py-1 bg-gray-50 text-gray-600 rounded text-xs">
                        {field.field_label}
                        {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Transitions */}
              {transitions?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">Actions:</div>
                  <div className="flex flex-wrap gap-2">
                    {transitions.map(t => (
                      <span 
                        key={t.id} 
                        className={cn(
                          "px-2 py-1 rounded text-xs font-medium",
                          t.action_color === 'green' && "bg-green-100 text-green-700",
                          t.action_color === 'red' && "bg-red-100 text-red-700",
                          t.action_color === 'blue' && "bg-blue-100 text-blue-700",
                          t.action_color === 'yellow' && "bg-yellow-100 text-yellow-700",
                          !t.action_color && "bg-gray-100 text-gray-700"
                        )}
                      >
                        {t.action_label || t.action_name}
                        {t.to_step_id && <ArrowRight size={12} className="inline ml-1" />}
                        {!t.to_step_id && <CheckCircle2 size={12} className="inline ml-1" />}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-1">
              <button
                onClick={onAddField}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Add field"
              >
                <Plus size={16} />
              </button>
              <button
                onClick={onEdit}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Edit step"
              >
                <Edit3 size={16} />
              </button>
              <button
                onClick={onDelete}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete step"
              >
                <Trash2 size={16} />
              </button>
              
              {/* Add transition button */}
              <div className="relative">
                <button
                  onClick={() => setShowTransitionMenu(!showTransitionMenu)}
                  className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                  title="Add transition"
                >
                  <ArrowRight size={16} />
                </button>
                
                {showTransitionMenu && (
                  <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="p-2 border-b border-gray-100 text-xs text-gray-500">Add Transition</div>
                    <button
                      onClick={() => {
                        onAddTransition(null, 'complete');
                        setShowTransitionMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <CheckCircle2 size={14} className="text-green-600" />
                      Complete Workflow
                    </button>
                    {nextSteps?.map(ns => (
                      <button
                        key={ns.id}
                        onClick={() => {
                          onAddTransition(ns.id, 'approve');
                          setShowTransitionMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <ArrowRight size={14} className="text-blue-600" />
                        Go to: {ns.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MODALS
// =============================================================================

const AddStepModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    step_type: 'task',
    assignment_type: 'role',
    assigned_role: 'Manager',
    is_required: true,
    requires_signoff: false,
    due_days: null
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium">Add Step</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Review Documents"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              placeholder="Optional description"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Step Type</label>
              <select
                value={formData.step_type}
                onChange={e => setFormData({ ...formData, step_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {STEP_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignment</label>
              <select
                value={formData.assignment_type}
                onChange={e => setFormData({ ...formData, assignment_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {ASSIGNMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {formData.assignment_type === 'role' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <input
                type="text"
                value={formData.assigned_role || ''}
                onChange={e => setFormData({ ...formData, assigned_role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Manager, Admin, Analyst"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Days</label>
            <input
              type="number"
              value={formData.due_days || ''}
              onChange={e => setFormData({ ...formData, due_days: e.target.value ? parseInt(e.target.value) : null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Days to complete (optional)"
              min="1"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.requires_signoff}
                onChange={e => setFormData({ ...formData, requires_signoff: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Requires Sign-off</span>
            </label>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Step
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditStepModal = ({ step, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: step.name || '',
    description: step.description || '',
    step_type: step.step_type || 'task',
    assignment_type: step.assignment_type || 'role',
    assigned_role: step.assigned_role || '',
    is_required: step.is_required ?? true,
    requires_signoff: step.requires_signoff || false,
    due_days: step.due_days || null
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium">Edit Step</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Step Type</label>
              <select
                value={formData.step_type}
                onChange={e => setFormData({ ...formData, step_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {STEP_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignment</label>
              <select
                value={formData.assignment_type}
                onChange={e => setFormData({ ...formData, assignment_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {ASSIGNMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {formData.assignment_type === 'role' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <input
                type="text"
                value={formData.assigned_role}
                onChange={e => setFormData({ ...formData, assigned_role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Days</label>
            <input
              type="number"
              value={formData.due_days || ''}
              onChange={e => setFormData({ ...formData, due_days: e.target.value ? parseInt(e.target.value) : null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="1"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.requires_signoff}
                onChange={e => setFormData({ ...formData, requires_signoff: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Requires Sign-off</span>
            </label>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddFieldModal = ({ stepId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    field_key: '',
    field_label: '',
    field_type: 'text',
    is_required: false,
    placeholder: '',
    help_text: '',
    field_options: []
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Generate field_key from label if not provided
    const fieldKey = formData.field_key || formData.field_label.toLowerCase().replace(/\s+/g, '_');
    onSave({
      ...formData,
      field_key: fieldKey
    });
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium">Add Custom Field</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Label</label>
            <input
              type="text"
              value={formData.field_label}
              onChange={e => setFormData({ ...formData, field_label: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Approval Notes"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
            <select
              value={formData.field_type}
              onChange={e => setFormData({ ...formData, field_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {FIELD_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
            <input
              type="text"
              value={formData.placeholder}
              onChange={e => setFormData({ ...formData, placeholder: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Placeholder text (optional)"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Help Text</label>
            <input
              type="text"
              value={formData.help_text}
              onChange={e => setFormData({ ...formData, help_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Help text shown below field (optional)"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_required"
              checked={formData.is_required}
              onChange={e => setFormData({ ...formData, is_required: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_required" className="text-sm text-gray-700">Required field</label>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Field
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateTemplateModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    entity_types: [],
    color: 'blue'
  });
  
  const toggleEntityType = (type) => {
    setFormData(prev => ({
      ...prev,
      entity_types: prev.entity_types.includes(type)
        ? prev.entity_types.filter(t => t !== type)
        : [...prev.entity_types, type]
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium">Create Workflow Template</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., RFQ Response Process"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              placeholder="Describe what this workflow is for"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
            <div className="flex flex-wrap gap-2">
              {ENTITY_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => toggleEntityType(type.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-sm transition-colors",
                    formData.entity_types.includes(type.value)
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <div className="flex gap-2">
              {COLORS.map(color => (
                <button
                  key={color.name}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: color.name })}
                  className={cn(
                    "w-8 h-8 rounded-lg transition-transform",
                    color.bg,
                    formData.color === color.name && "ring-2 ring-offset-2 ring-blue-500 scale-110"
                  )}
                />
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Template
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function WorkflowsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['workflow-templates'],
    queryFn: () => workflowApi.listTemplates({ includeInactive: true })
  });
  
  // Fetch selected template details
  const { data: templateDetails } = useQuery({
    queryKey: ['workflow-template', selectedTemplate?.id],
    queryFn: () => workflowApi.getTemplate(selectedTemplate?.id),
    enabled: !!selectedTemplate?.id
  });
  
  // Create template mutation
  const createMutation = useMutation({
    mutationFn: workflowApi.createTemplate,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['workflow-templates']);
      toast.success('Template created successfully');
      setShowCreateModal(false);
      setSelectedTemplate(data.data);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  });
  
  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: workflowApi.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-templates']);
      toast.success('Template deleted');
      setSelectedTemplate(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  });
  
  const handleDeleteTemplate = (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      deleteMutation.mutate(templateId);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {/* Header with Analytics Link */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Workflow className="w-5 h-5 text-blue-600" />
          <h1 className="font-semibold text-gray-900">Workflow Templates</h1>
        </div>
        <button
          onClick={() => navigate('/workflows/analytics')}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          data-testid="analytics-link"
        >
          <BarChart3 className="w-4 h-4" />
          Analytics Dashboard
        </button>
      </div>
      
      <div className="flex-1 flex">
        {/* Template List */}
        <TemplateList
          templates={templatesData?.data || []}
          selectedId={selectedTemplate?.id}
          onSelect={setSelectedTemplate}
          onCreateNew={() => setShowCreateModal(true)}
        />
        
        {/* Template Editor */}
        <TemplateEditor
          template={templateDetails?.data || selectedTemplate}
          onUpdate={(data) => {
            queryClient.invalidateQueries(['workflow-template', selectedTemplate?.id]);
          }}
          onDelete={handleDeleteTemplate}
        />
      </div>
      
      {/* Create Template Modal */}
      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onSave={(data) => createMutation.mutate(data)}
        />
      )}
    </div>
  );
}
